import { useQuery } from "@apollo/client";
import type { ClientServiceProfileFragmentFragment } from "@/__generated__/graphql";
import {
  ClientServiceProfileByFiltersDocument,
  ClientServiceProfileFragmentFragmentDoc,
  ClientServiceProfileStatusesEnum,
} from "@/__generated__/graphql";
import { useAuthState } from "@/store/auth";
import type { ServiceProfileItem } from "@/containers/profile/service-profile/lib";
import {
  mapServiceProfileToItem,
  ServiceProfileServiceIdMap,
  type ServiceProfileType,
} from "@/containers/profile/service-profile/lib";
import { useMemo } from "react";
import { getFragmentData } from "@/__generated__";

export const useClientServiceProfileByFiltersQuery = (
  type?: ServiceProfileType,
) => {
  const clientId = useAuthState((state) => state.data.userInfo.id);

  const serviceId = type ? ServiceProfileServiceIdMap[type] : undefined;

  const result = useQuery(ClientServiceProfileByFiltersDocument, {
    skip: !clientId,
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        clientId,
        status: ClientServiceProfileStatusesEnum.Active,
        serviceId,
      },
    },
  });

  const mappedProfiles = useMemo(() => {
    const defaultProfile = Object.entries(ServiceProfileServiceIdMap).reduce(
      (profileData, [type, serviceId]) => {
        const profileType =
          result.data?.clientServiceProfileByFilters.clientServiceProfiles.find(
            (item) => {
              const profile = getFragmentData(
                ClientServiceProfileFragmentFragmentDoc,
                item,
              );
              return profile.service.id === serviceId && profile.primary;
            },
          ) as ClientServiceProfileFragmentFragment | undefined;

        if (profileType) {
          profileData[type as ServiceProfileType] = mapServiceProfileToItem(
            profileType,
            type as ServiceProfileType,
          );
        }

        return profileData;
      },
      {
        pet: null,
        elder: null,
        baby: null,
      } as Record<ServiceProfileType, ServiceProfileItem | null>,
    );

    if (!type) {
      return { defaultProfile, profiles: [] };
    }

    const profiles = (
      result.data?.clientServiceProfileByFilters.clientServiceProfiles ?? []
    )
      .map((item) => {
        const profile = getFragmentData(
          ClientServiceProfileFragmentFragmentDoc,
          item,
        );
        return mapServiceProfileToItem(profile, type);
      })
      .sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));

    return { defaultProfile, profiles };
  }, [result.data?.clientServiceProfileByFilters.clientServiceProfiles, type]);

  return { ...result, ...mappedProfiles };
};
